import React, {FC, Suspense, useMemo, lazy} from 'react'
import {Spinner} from 'react-bootstrap'
import {KTSVG} from '../../../../../_metronic/helpers'
import {ChartCardProps} from './types'
import LoadingFallback from './LoadingFallback'

const ReactApexChart = lazy(() => import('react-apexcharts'))

const ChartCard: FC<ChartCardProps> = React.memo(
  ({title, dateRange, totalValue, graphData, createChartOptions, hasData, loading}) => {
    const chartConfig = useMemo(() => {
      if (!graphData) return null
      return createChartOptions(graphData)
    }, [graphData, createChartOptions])

    if (loading) {
      return (
        <div className='card h-100'>
          <div className='card-header border-0 pt-5 pb-0'>
            <div className='card-title flex-column'>
              <h3 className='fw-bold fs-5 mb-1'>{title}</h3>
              <span className='text-muted fs-7'>{dateRange}</span>
            </div>
            <div className='card-toolbar'>
              <Spinner size='sm' animation='border' variant='primary' />
            </div>
          </div>
          <div className='card-body pt-4 d-flex align-items-center justify-content-center'>
            <LoadingFallback height={180} text='Loading data...' />
          </div>
        </div>
      )
    }

    if (!hasData) {
      return (
        <div className='card h-100'>
          <div className='card-body d-flex flex-column align-items-center justify-content-center py-10'>
            <KTSVG
              path='/media/icons/duotune/files/fil012.svg'
              className='svg-icon-4x svg-icon-gray-400 mb-4'
            />
            <p className='text-muted fs-6 fw-semibold'>No data available</p>
          </div>
        </div>
      )
    }

    return (
      <div className='chart-card card h-100'>
        <div className='card-header border-0 pt-5 pb-0'>
          <div className='card-title flex-column'>
            <h3 className='fw-bold fs-5 mb-1'>{title}</h3>
            <span className='text-muted fs-7'>{dateRange}</span>
          </div>
          {totalValue !== undefined && (
            <div className='card-toolbar'>
              <span className='fs-2 fw-bold text-primary'>{totalValue}</span>
            </div>
          )}
        </div>

        <div className='card-body pt-4'>
          <Suspense fallback={<LoadingFallback height={180} />}>
            {chartConfig && (
              <ReactApexChart
                options={chartConfig.options}
                series={chartConfig.series}
                type='bar'
                height={180}
              />
            )}
          </Suspense>
        </div>
      </div>
    )
  }
)

ChartCard.displayName = 'ChartCard'

export default ChartCard
